import {
  Document, Page, View, Text, Svg, Rect, Line, G, Path, Font, StyleSheet, pdf,
} from '@react-pdf/renderer';
import type { MaterialResult } from '../cutting-calc/CalcResultView';
import type { UsedStock, CalcResult } from '@app/linecut-core';
import type { ReportSettings } from '../cutting-calc/useReportSettings';

Font.register({
  family: 'PTSans',
  fonts: [
    { src: '/fonts/PTSans-Regular.ttf', fontWeight: 'normal' },
    { src: '/fonts/PTSans-Bold.ttf', fontWeight: 'bold' },
  ],
});

export interface PdfFile {
  bytes: Uint8Array;
  name: string;
  base64: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + chunk, bytes.length)));
  }
  return btoa(binary);
}

function fmtN(n: number): string { return String(Math.round(n)); }
function fmt(n: number): string { return `${Math.round(n)}мм`; }

function groupByKey<T>(items: T[], key: (item: T) => string): Array<{ count: number; item: T }> {
  const groups = new Map<string, { count: number; item: T }>();
  for (const item of items) {
    const k = key(item);
    const g = groups.get(k);
    if (g) g.count++;
    else groups.set(k, { count: 1, item });
  }
  return Array.from(groups.values());
}

function declPiece(n: number): string {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 19) return 'деталей';
  if (mod10 === 1) return 'деталь';
  if (mod10 >= 2 && mod10 <= 4) return 'детали';
  return 'деталей';
}

// ─── Colors ───────────────────────────────────────────────────────────────────

const RED   = '#CE2B27';
const BLACK = '#000000';
const GRAY  = '#888B8D';
const BG    = '#f3f4f6';
const TRACK = '#e5e7eb';
const GREEN = '#22c55e';
const WARN  = '#d97706';
const WHITE = '#ffffff';

// TODO: раскомментировать цвета отрезков когда понадобится визуализация
// const PIECE_COLORS: Array<{ fill: string; opacity: number }> = [
//   { fill: RED, opacity: 1.00 },
//   { fill: RED, opacity: 0.68 },
//   { fill: RED, opacity: 0.45 },
//   { fill: RED, opacity: 0.28 },
//   { fill: RED, opacity: 0.16 },
// ];
const PIECE_COLORS: Array<{ fill: string; opacity: number }> = [
  { fill: BG, opacity: 1 },
  { fill: BG, opacity: 1 },
  { fill: BG, opacity: 1 },
  { fill: BG, opacity: 1 },
  { fill: BG, opacity: 1 },
];

// ─── Bar constants ────────────────────────────────────────────────────────────

const SVG_W    = 1000;    // SVG coordinate width
const BAR_H    = 20;      // SVG coordinate bar height
const BAR_H_PT = 14;      // rendered bar height, pt
const LABEL_H  = 13;      // height reserved below bar for labels, pt

// A4 content width in pt (595/842 − 2 × 15mm ≈ 2 × 42.5pt)
const CONTENT_W: Record<'portrait' | 'landscape', number> = {
  portrait:  510,
  landscape: 757,
};

// Clamp remainder display to this many pt (≈35px at typical screen size)
const REM_CLAMP_PT = 30;

// ─── Bar computation ──────────────────────────────────────────────────────────

interface BarSeg {
  x: number; w: number;             // raw fractions 0..1
  fill: string; fillOpacity: number;
  isRem?: boolean;
}
interface BarLabel {
  cx: number;                       // raw fraction 0..1
  text: string;
  dim?: boolean;
  isRem?: boolean;
}
interface BarCut {
  left: number; right: number;      // raw fractions 0..1
  hasBlade: boolean;
}

function computeBar(
  stock: UsedStock,
  fromZero: boolean,
  barW: number,
): {
  segs: BarSeg[];
  cuts: BarCut[];
  labels: BarLabel[];
  isRemClamped: boolean;
  displayRemFrac: number;
  leftScale: number;
  amp: number;
} {
  const trimPerSide = stock.trimWaste / 2;
  const n = stock.pieces.length;
  const numCuts = stock.remainder > 0 ? n : Math.max(0, n - 1);
  const bladePerCut = numCuts > 0 && stock.bladeWaste > 0 ? stock.bladeWaste / numCuts : 0;

  function f(v: number) { return v / stock.length; }

  const naturalRemFrac = f(stock.remainder);
  const isRemClamped = stock.remainder > 0 && naturalRemFrac * barW > REM_CLAMP_PT;
  const displayRemFrac = isRemClamped ? REM_CLAMP_PT / barW : naturalRemFrac;
  const leftScale = isRemClamped && naturalRemFrac < 1
    ? (1 - displayRemFrac) / (1 - naturalRemFrac)
    : 1;
  const amp = (4 * SVG_W) / barW;

  const segs: BarSeg[]   = [];
  const cuts: BarCut[]   = [];
  const labels: BarLabel[] = [];

  // Background
  segs.push({ x: 0, w: 1, fill: BG, fillOpacity: 1 });

  if (fromZero) {
    // Individual-piece mode: cumulative-distance labels at right edge of each piece
    const pieceGroups: Array<{ length: number; partName: string; count: number }> = [];
    for (const piece of stock.pieces) {
      const last = pieceGroups[pieceGroups.length - 1];
      if (last && last.length === piece.length && last.partName === piece.partName) last.count++;
      else pieceGroups.push({ length: piece.length, partName: piece.partName, count: 1 });
    }
    const flatPieces: Array<{ length: number; groupIdx: number }> = [];
    for (const [gi, group] of pieceGroups.entries()) {
      for (let k = 0; k < group.count; k++) flatPieces.push({ length: group.length, groupIdx: gi });
    }

    let cur = 0;
    let cumDist = 0;
    if (trimPerSide > 0) {
      segs.push({ x: f(cur), w: f(trimPerSide), fill: TRACK, fillOpacity: 1 });
      cur += trimPerSide;
      cumDist += trimPerSide;
    }
    flatPieces.forEach((piece, pi) => {
      const isLast = pi === flatPieces.length - 1;
      const hasCutAfter = !isLast || stock.remainder > 0;
      const pc = PIECE_COLORS[piece.groupIdx % PIECE_COLORS.length];
      segs.push({ x: f(cur), w: f(piece.length), fill: pc.fill, fillOpacity: pc.opacity });
      cur += piece.length;
      cumDist += piece.length;
      labels.push({ cx: f(cur), text: fmtN(cumDist) });
      if (hasCutAfter) {
        cuts.push({ left: f(cur), right: f(cur + bladePerCut), hasBlade: bladePerCut > 0 });
        cur += bladePerCut;
        cumDist += bladePerCut;
      }
    });
    if (trimPerSide > 0) {
      segs.push({ x: f(cur), w: f(trimPerSide), fill: TRACK, fillOpacity: 1 });
      cur += trimPerSide;
    }
    if (stock.remainder > 0) {
      segs.push({
        x: f(cur), w: f(stock.remainder),
        fill: stock.isBusinessRemainder ? GREEN : BG,
        fillOpacity: stock.isBusinessRemainder ? 0.12 : 1,
        isRem: true,
      });
      // No remainder label in from-zero mode (matches CalcResultView)
    }
  } else {
    // Merged mode: group consecutive same-length same-name pieces
    const pieceGroups: Array<{ length: number; partName: string; count: number }> = [];
    for (const piece of stock.pieces) {
      const last = pieceGroups[pieceGroups.length - 1];
      if (last && last.length === piece.length && last.partName === piece.partName) last.count++;
      else pieceGroups.push({ length: piece.length, partName: piece.partName, count: 1 });
    }

    let cur = 0;
    if (trimPerSide > 0) {
      segs.push({ x: f(cur), w: f(trimPerSide), fill: TRACK, fillOpacity: 1 });
      cur += trimPerSide;
    }
    pieceGroups.forEach((group, gi) => {
      const groupStart = cur;
      const isLastGroup = gi === pieceGroups.length - 1;
      const hasCutAfter = !isLastGroup || stock.remainder > 0;
      const groupSpan = group.count * group.length + (group.count - 1) * bladePerCut;
      const pc = PIECE_COLORS[gi % PIECE_COLORS.length];
      segs.push({ x: f(groupStart), w: f(groupSpan), fill: pc.fill, fillOpacity: pc.opacity });
      labels.push({
        cx: f(groupStart + groupSpan / 2),
        text: group.count > 1 ? `${group.count}×${fmtN(group.length)}` : fmtN(group.length),
      });
      cur += groupSpan;
      if (hasCutAfter) {
        cuts.push({ left: f(cur), right: f(cur + bladePerCut), hasBlade: bladePerCut > 0 });
        cur += bladePerCut;
      }
    });
    if (trimPerSide > 0) {
      segs.push({ x: f(cur), w: f(trimPerSide), fill: TRACK, fillOpacity: 1 });
      cur += trimPerSide;
    }
    if (stock.remainder > 0) {
      segs.push({
        x: f(cur), w: f(stock.remainder),
        fill: stock.isBusinessRemainder ? GREEN : BG,
        fillOpacity: stock.isBusinessRemainder ? 0.12 : 1,
        isRem: true,
      });
      labels.push({ cx: f(cur + stock.remainder / 2), text: fmtN(stock.remainder), dim: true, isRem: true });
    }
  }

  return { segs, cuts, labels, isRemClamped, displayRemFrac, leftScale, amp };
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = StyleSheet.create({
  page: {
    fontFamily: 'PTSans',
    fontSize: 10,
    color: BLACK,
    paddingTop: '15mm',
    paddingBottom: '15mm',
    paddingLeft: '15mm',
    paddingRight: '15mm',
    backgroundColor: WHITE,
  },
  summaryBox: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    borderWidth: 1,
    borderColor: TRACK,
    borderStyle: 'solid',
    borderRadius: 4,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginBottom: 14,
  },
  summaryTitle: {
    fontWeight: 'bold',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginRight: 14,
  },
  summaryVal: {
    fontWeight: 'bold',
    fontSize: 14,
    marginRight: 4,
  },
  summaryLabel: {
    fontSize: 9,
    color: GRAY,
    marginRight: 14,
  },
  summaryLabelRight: {
    fontSize: 9,
    color: GRAY,
    marginLeft: 'auto',
    marginRight: 0,
  },
  materialSection: {
    marginBottom: 16,
  },
  materialTitle: {
    fontWeight: 'bold',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 6,
  },
  statsRow: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: TRACK,
    borderStyle: 'solid',
    borderRadius: 4,
    marginBottom: 8,
  },
  statCell: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderRightWidth: 1,
    borderRightColor: TRACK,
    borderRightStyle: 'solid',
  },
  statCellLast: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 6,
  },
  statValue: {
    fontWeight: 'bold',
    fontSize: 14,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: 8,
    color: GRAY,
    marginTop: 2,
    textAlign: 'center',
  },
  stockCard: {
    borderWidth: 1,
    borderColor: TRACK,
    borderStyle: 'solid',
    borderRadius: 4,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginBottom: 6,
  },
  stockHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  stockCount: {
    fontWeight: 'bold',
    fontSize: 10,
    color: GRAY,
    marginRight: 4,
  },
  stockLength: {
    fontWeight: 'bold',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginRight: 6,
  },
  stockMeta: {
    fontSize: 9,
    color: GRAY,
    marginLeft: 'auto',
  },
  pieceTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
  },
  pieceTag: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 2,
    paddingHorizontal: 6,
    backgroundColor: BG,
    borderWidth: 1,
    borderColor: TRACK,
    borderStyle: 'solid',
    borderRadius: 3,
    fontSize: 8,
    marginRight: 4,
    marginBottom: 4,
  },
  pieceDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 4,
  },
  schemeBox: {
    borderWidth: 1,
    borderColor: TRACK,
    borderStyle: 'solid',
    borderRadius: 4,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginTop: 4,
  },
  schemeTitle: {
    fontWeight: 'bold',
    fontSize: 9,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 5,
    color: GRAY,
  },
  schemeText: {
    fontFamily: 'Courier',
    fontSize: 8,
    lineHeight: 1.6,
  },
});

// ─── BarRow ───────────────────────────────────────────────────────────────────
// Bar SVG (rects + cuts, no text) + label row rendered as react-pdf Text elements
// to avoid SVG text scaling issues.

function BarRow({ stock, fromZero, barW }: { stock: UsedStock; fromZero: boolean; barW: number }) {
  const { segs, cuts, labels, isRemClamped, displayRemFrac, leftScale, amp } = computeBar(stock, fromZero, barW);
  const period = 8;

  function segX(seg: BarSeg): number {
    return seg.isRem ? (1 - displayRemFrac) * SVG_W : seg.x * leftScale * SVG_W;
  }
  function segW(seg: BarSeg): number {
    return seg.isRem ? displayRemFrac * SVG_W : seg.w * leftScale * SVG_W;
  }
  function lx(frac: number): number { return frac * leftScale * SVG_W; }
  function lblPct(lbl: BarLabel): number {
    return lbl.isRem
      ? (1 - displayRemFrac / 2) * 100
      : lbl.cx * leftScale * 100;
  }

  return (
    <View style={{ marginBottom: 2 }}>
      <Svg
        viewBox={`0 0 ${SVG_W} ${BAR_H}`}
        style={{ width: '100%', height: BAR_H_PT }}
        preserveAspectRatio="none"
      >
        {segs.map((seg, i) => {
          const x0 = segX(seg);
          const x1 = x0 + segW(seg);

          if (seg.isRem && isRemClamped) {
            // Squiggly right edge to indicate clamped remainder
            let d = `M ${x0} ${BAR_H} L ${x0} 0 L ${x1} 0`;
            let y = 0;
            let sign = 1;
            while (y < BAR_H) {
              const ny = Math.min(y + period, BAR_H);
              d += ` Q ${x1 + sign * amp} ${(y + ny) / 2} ${x1} ${ny}`;
              y = ny;
              sign = -sign;
            }
            d += ' Z';
            return <Path key={i} d={d} fill={seg.fill} fillOpacity={seg.fillOpacity} />;
          }

          return (
            <Rect key={i}
              x={x0} y={0}
              width={Math.max(x1 - x0, 0.5)}
              height={BAR_H}
              fill={seg.fill}
              fillOpacity={seg.fillOpacity}
            />
          );
        })}

        {cuts.map((cut, i) => {
          const xl = lx(cut.left);
          const xr = lx(cut.right);
          const xc = (xl + xr) / 2;
          const edgeX = cut.hasBlade ? xl : xc;
          return (
            <G key={i}>
              {cut.hasBlade && (
                <Rect x={xl} y={0} width={xr - xl} height={BAR_H} fill={GRAY} fillOpacity={0.22} />
              )}
              <Line x1={edgeX} y1={0} x2={edgeX} y2={BAR_H}
                stroke={BLACK} strokeWidth={1} strokeDasharray="4 3" />
              {cut.hasBlade && (
                <Line x1={xr} y1={0} x2={xr} y2={BAR_H}
                  stroke={BLACK} strokeWidth={1} strokeDasharray="4 3" />
              )}
            </G>
          );
        })}

        {isRemClamped ? (
          <>
            <Line x1={0} y1={0} x2={SVG_W} y2={0} stroke={TRACK} strokeWidth={1} />
            <Line x1={0} y1={BAR_H} x2={SVG_W} y2={BAR_H} stroke={TRACK} strokeWidth={1} />
            <Line x1={0} y1={0} x2={0} y2={BAR_H} stroke={TRACK} strokeWidth={1} />
          </>
        ) : (
          <Rect x={0} y={0} width={SVG_W} height={BAR_H}
            fill="none" stroke={TRACK} strokeWidth={1} />
        )}
      </Svg>

      {/* Labels as react-pdf Text — avoids SVG text scaling issues */}
      <View style={{ position: 'relative', height: LABEL_H }}>
        {labels.map((lbl, i) => {
          const pct = Math.max(0, Math.min(96, lblPct(lbl)));
          return (
            <View key={i} style={{
              position: 'absolute',
              left: `${pct}%`,
              top: 1,
              marginLeft: -16,
              width: 32,
            }}>
              <Text style={{ fontSize: 6.5, textAlign: 'center', color: lbl.dim ? GRAY : BLACK }}>
                {lbl.text}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}

// ─── StockCard ────────────────────────────────────────────────────────────────

function StockCard({
  stock, count, settings, barW,
}: {
  stock: UsedStock; count: number; settings: ReportSettings; barW: number;
}) {
  const pieceGroups: Array<{ length: number; partName: string; count: number }> = [];
  for (const piece of stock.pieces) {
    const last = pieceGroups[pieceGroups.length - 1];
    if (last && last.length === piece.length && last.partName === piece.partName) last.count++;
    else pieceGroups.push({ length: piece.length, partName: piece.partName, count: 1 });
  }

  return (
    <View style={S.stockCard} wrap={false}>
      <View style={S.stockHeader}>
        {count > 1 && <Text style={S.stockCount}>{count}×</Text>}
        <Text style={S.stockLength}>{fmt(stock.length)}</Text>
        <Text style={S.stockMeta}>
          {stock.pieces.length} {declPiece(stock.pieces.length)}
          {stock.remainder > 0 ? ` · отход ${fmt(stock.remainder)}` : ''}
        </Text>
      </View>

      {settings.showScheme && (
        <BarRow stock={stock} fromZero={settings.fromZero} barW={barW} />
      )}

      {settings.showPieceTags && (
        <View style={S.pieceTags}>
          {pieceGroups.map((group, gi) => {
            const pc = PIECE_COLORS[gi % PIECE_COLORS.length];
            return (
              <View key={gi} style={S.pieceTag}>
                <View style={[S.pieceDot, { backgroundColor: pc.fill, opacity: pc.opacity }]} />
                <Text>
                  {group.count > 1 ? `${group.count}× ` : ''}
                  {group.partName !== String(group.length)
                    ? `${group.partName} · ${fmt(group.length)}`
                    : fmt(group.length)}
                </Text>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
}

// ─── Text scheme builder ──────────────────────────────────────────────────────

function buildTextScheme(
  stocks: UsedStock[],
  blade: number,
  trim: number,
  fromZero: boolean,
): string {
  const groups = groupByKey(stocks, (s) => `${s.length}|${s.material}|${s.pieces.map((p) => p.length).join(',')}`);
  const primaryLines: string[] = [];
  const fromZeroLines: string[] = [];

  for (const { count, item: rep } of groups) {
    const n = rep.pieces.length;
    const numCuts = rep.remainder > 0 ? n : Math.max(0, n - 1);
    const bladePerCut = numCuts > 0 && blade > 0 ? blade : 0;

    const pieceParts = rep.pieces.map((piece, i) => {
      const seg = `[ ${fmtN(piece.length)}мм ]`;
      return i === 0 ? seg : `${blade > 0 ? `{${fmtN(blade)}}` : ''}${seg}`;
    });
    primaryLines.push(`${count}x ${pieceParts.join('')}`);

    if (fromZero) {
      const positions: number[] = [];
      let cursor = trim;
      rep.pieces.forEach((piece, i) => {
        cursor += piece.length;
        const hasCutAfter = i < n - 1 || rep.remainder > 0;
        if (hasCutAfter) {
          positions.push(cursor + bladePerCut / 2);
          cursor += bladePerCut;
        }
      });
      if (positions.length > 0) {
        fromZeroLines.push(`${count}x | ${positions.map((p) => `${fmtN(p)}мм`).join(' | ')} |`);
      }
    }
  }

  return fromZero && fromZeroLines.length > 0
    ? `${primaryLines.join('\n')}\n\nРазмеры от нуля:\n${fromZeroLines.join('\n')}`
    : primaryLines.join('\n');
}

// ─── MaterialSection ──────────────────────────────────────────────────────────

function MaterialSection({
  material, result, blade, trim, settings, barW, pageBreak,
}: {
  material: string; result: CalcResult; blade: number; trim: number;
  settings: ReportSettings; barW: number; pageBreak?: boolean;
}) {
  const { totalBars, totalPieces, efficiency, usedStocks, totalWasteLength } = result;
  const effColor = efficiency >= 90 ? GREEN : efficiency >= 75 ? WARN : RED;
  const stockGroups = groupByKey(usedStocks, (s) => `${s.length}|${s.pieces.map((p) => p.length).join(',')}`);
  const textScheme = buildTextScheme(usedStocks, blade, trim, settings.fromZero);

  const stats: Array<{ value: string; label: string; color: string }> = [
    { value: String(totalBars),                      label: 'заготовок',         color: BLACK },
    { value: String(totalPieces),                    label: 'деталей размещено', color: BLACK },
    { value: `${efficiency}%`,                       label: 'эффективность',     color: effColor },
    { value: `${Math.round(totalWasteLength)} мм`,   label: 'общий отход',       color: BLACK },
  ];

  return (
    <View break={pageBreak ?? false} style={S.materialSection}>
      <Text style={S.materialTitle}>{material}</Text>

      <View style={S.statsRow}>
        {stats.map((stat, i) => (
          <View key={i} style={i < stats.length - 1 ? S.statCell : S.statCellLast}>
            <Text style={[S.statValue, { color: stat.color }]}>{stat.value}</Text>
            <Text style={S.statLabel}>{stat.label}</Text>
          </View>
        ))}
      </View>

      {stockGroups.map(({ count, item: stock }) => (
        <StockCard
          key={`${stock.length}|${stock.pieces.map((p) => p.length).join(',')}`}
          stock={stock} count={count} settings={settings} barW={barW}
        />
      ))}

      {settings.showTextScheme && (
        <View style={S.schemeBox}>
          <Text style={S.schemeTitle}>Схема раскроя</Text>
          <Text style={S.schemeText}>{textScheme}</Text>
        </View>
      )}
    </View>
  );
}

// ─── CuttingDocument ─────────────────────────────────────────────────────────

function CuttingDocument({
  results, blade, trim, settings, orientation,
}: {
  results: MaterialResult[]; blade: number; trim: number;
  settings: ReportSettings; orientation: 'portrait' | 'landscape';
}) {
  const barW = CONTENT_W[orientation];
  const totalBars   = results.reduce((s, r) => s + r.result.totalBars, 0);
  const totalPieces = results.reduce((s, r) => s + r.result.totalPieces, 0);
  const weightedEff = totalBars > 0
    ? Math.round(results.reduce((s, r) => s + r.result.efficiency * r.result.totalBars, 0) / totalBars)
    : 0;
  const effColor = weightedEff >= 90 ? GREEN : weightedEff >= 75 ? WARN : RED;

  return (
    <Document>
      <Page size="A4" orientation={orientation} style={S.page}>
        <View style={S.summaryBox}>
          <Text style={S.summaryTitle}>Итого</Text>
          <Text style={S.summaryVal}>{totalBars}</Text>
          <Text style={S.summaryLabel}>заготовок</Text>
          <Text style={S.summaryVal}>{totalPieces}</Text>
          <Text style={S.summaryLabel}>деталей размещено</Text>
          <Text style={[S.summaryVal, { color: effColor }]}>{weightedEff}%</Text>
          <Text style={S.summaryLabel}>эффективность</Text>
          <Text style={S.summaryLabelRight}>
            лезвие {blade} мм · торцовка {trim} мм
          </Text>
        </View>

        {results.map(({ material, result }, i) => (
          <MaterialSection
            key={material}
            material={material} result={result}
            blade={blade} trim={trim}
            settings={settings} barW={barW}
            pageBreak={i > 0}
          />
        ))}
      </Page>
    </Document>
  );
}

// ─── Public API ───────────────────────────────────────────────────────────────

export async function generateCuttingPdf(
  results: MaterialResult[],
  blade: number,
  trim: number,
  settings: ReportSettings,
  orientation: 'portrait' | 'landscape',
): Promise<PdfFile> {
  const blob = await pdf(
    <CuttingDocument
      results={results}
      blade={blade}
      trim={trim}
      settings={settings}
      orientation={orientation}
    />,
  ).toBlob();
  const arrayBuffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  return {
    bytes,
    name: 'Схема раскроя.pdf',
    base64: uint8ToBase64(bytes),
  };
}
